import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { CustomLink } from 'msp-components'
import {
  buildSuggestionUrl,
  useCommerceSFRAAPI,
  useMultiSite,
  useCookie
} from 'msp-integrations'
import { ContentSearchProps, ContentResultsProps } from './ContentSearch.types'
import { ContentSearchStyles } from './ContentSearch.styles'

const ContentSearch = ({
  className,
  searchQuery,
  onSearching,
  onSearch
}: ContentSearchProps) => {
  const { eventTracking } = useCookie()
  const [searchTerm, setSearchTerm] = useState(searchQuery || '')
  const [rawResults, setRawResults] = useState()
  const [results, setResults] = useState<Record<string, []>>({})
  const [hasResults, setHasResults] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const SFRAClient = useCommerceSFRAAPI()
  const { buildUrl } = useMultiSite()

  const handleResponse = (
    error: any,
    res?: ContentResultsProps['rawResults']
  ) => {
    let resultsToSet: Record<string, []> = {}
    if (res) {
      resultsToSet = res?.hits
      setRawResults(res)
      setResults(resultsToSet)
      setHasResults(res?.count !== 0)
    }
    onSearching ? onSearching(false) : setIsSearching(false)
    if (onSearch) onSearch(resultsToSet, searchTerm)
  }

  const searchResults = () => {
    onSearching ? onSearching(true) : setIsSearching(true)
    SFRAClient?.OcapiSearch?.searchContent({ query: { q: searchQuery } })
      .then((res: any) => {
        handleResponse(res.error, res)
      })
      .catch((error: any) => {
        handleResponse(error)
      })
  }

  useEffect(() => {
    setSearchTerm(searchQuery)
    searchQuery?.length
      ? searchResults()
      : onSearching
      ? onSearching(false)
      : setIsSearching(false)

    if (searchQuery?.length > 0) {
      eventTracking('search', {
        search_query: searchQuery
      })
    }
  }, [searchQuery])

  return (
    <div id='contentResults' className={className}>
      {!isSearching && hasResults && (
        <ContentSearchResults
          rawResults={rawResults}
          results={results}
          buildUrl={buildUrl}
        />
      )}
    </div>
  )
}

/*
  Component to build content search results HTML
 */
const ContentSearchResults = ({
  rawResults,
  results,
  buildUrl
}: ContentResultsProps) => {
  const { formatMessage } = useIntl()
  return (
    <>
      <div className={ContentSearchStyles.contentTitle}>
        {formatMessage({
          id: 'msp.search.content.title',
          defaultMessage: 'Content'
        })}{' '}
        ({rawResults?.count})
      </div>
      <div className={ContentSearchStyles.wrapper}>
        {results.map((row: any) => (
          <div key={row.id} className={ContentSearchStyles.contentRowWrapper}>
            <div className={ContentSearchStyles.pageNameDiv}>{row.name}</div>
            <div className={ContentSearchStyles.descAndLinkWrapper}>
              <p className={ContentSearchStyles.description}>
                {row?.description?.substring(0, 300)}
              </p>
              <div className={ContentSearchStyles.linkWrapper}>
                <span className={ContentSearchStyles.rightArrowStyle}>
                  {'> '}
                </span>
                <CustomLink
                  className={ContentSearchStyles.contentLinkStyle}
                  colorScheme='primary'
                  href={buildSuggestionUrl('contentSuggestions', row, buildUrl)}
                >
                  {formatMessage({
                    id: 'msp.search.content.toLink',
                    defaultMessage: 'To Content'
                  })}
                </CustomLink>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  )
}

export default ContentSearch
